import type {
  segmentDetailResource,
  segmentMemberResource,
  segmentResource,
} from '@porotutu/api/features/crm/segments/resource'
import type { CrmSegmentMemberSort } from '@porotutu/http/vocabulary'
import { exportHrefFor, unwrap } from '@porotutu/react/api'
import type { ListMeta, ListQuery } from '@porotutu/react/paging'
import { keepPreviousData, queryOptions } from '@tanstack/react-query'
import { crm } from '@/api/client'
import { SEGMENTS } from '@/api/keys'
import type { CrmSegmentSort, SegmentKind } from '../vocabulary'

export type Segment = ReturnType<typeof segmentResource>
export type SegmentDetail = ReturnType<typeof segmentDetailResource>
export type SegmentMember = ReturnType<typeof segmentMemberResource>

export type SegmentPage = { data: Segment[]; meta: ListMeta }

export type SegmentListQuery = ListQuery<CrmSegmentSort, never> & { segment_kind?: SegmentKind }

export type SegmentMemberPage = { data: SegmentMember[]; meta: ListMeta }

export type SegmentMemberListQuery = ListQuery<CrmSegmentMemberSort, never>

export const segmentsQuery = (query: SegmentListQuery) =>
  queryOptions({
    queryKey: [...SEGMENTS, 'list', query] as const,
    queryFn: async () => unwrap(await crm.segments.get({ query })),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  })

export const segmentQuery = (id: string) =>
  queryOptions({
    queryKey: [...SEGMENTS, 'record', id] as const,
    queryFn: async () => unwrap(await crm.segments({ id }).get()),
    staleTime: 30_000,
  })

export const segmentMembersQuery = (id: string, query: SegmentMemberListQuery) =>
  queryOptions({
    queryKey: [...SEGMENTS, 'members', id, query] as const,
    queryFn: async () => unwrap(await crm.segments({ id }).members.get({ query })),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  })

export const exportHref = exportHrefFor<SegmentListQuery>('/api/v1/crm/segments/export', {
  always: [],
  viewed: ['segment_kind', 'search'],
})

export const memberExportHref = (id: string) =>
  exportHrefFor<SegmentMemberListQuery>(`/api/v1/crm/segments/${id}/members/export`, {
    always: [],
    viewed: ['search'],
  })
